import { getHiddenMsSinceLoad, recordClientDiagnosticEvent } from './clientDiagnostics';

type StartupPerfEntry = {
  name: string;
  time: number;
  delta: number;
  data?: Record<string, any>;
};

declare global {
  interface Window {
    __SSG_STARTUP_PERF__?: StartupPerfEntry[];
  }
}

const STORAGE_KEY = 'ssg_startup_perf';
const START_TIME = performance.now();

let isStartupPhaseReported = false;
const entries: StartupPerfEntry[] = [];
const STARTUP_PHASE_MARKS = ['api_first_response', 'ui_loader_ready', 'splash_removed'];

const SEGMENT_LOGS: { from: string; to: string; label: string }[] = [
  { from: 'splash_removed', to: 'auth_actions_ready', label: '转圈结束 → 登录/注册按钮' },
  { from: 'auth_bundle_load_start', to: 'auth_bundle_load_done', label: 'Auth 分包加载' },
  { from: 'splash_removed', to: 'chat_list_ready', label: '转圈结束 → 会话列表可见' },
];

function logSegmentsFor(name: string) {
  SEGMENT_LOGS.forEach(({ from, to, label }) => {
    if (to !== name) return;

    const fromAt = markAt(from);
    const toAt = markAt(to);
    if (fromAt === undefined || toAt === undefined) return;

    // eslint-disable-next-line no-console
    console.log(
      `[SSG_PERF] ${label}: ${Math.round(toAt - fromAt)}ms`,
      `(${from} @${Math.round(fromAt)} → ${to} @${Math.round(toAt)})`,
    );
  });
}

function isStartupPerfEnabled() {
  const params = new URLSearchParams(window.location.search);
  const isLocal = window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
    || window.location.hostname.startsWith('192.168.')
    || window.location.port !== '';

  return params.get('ssgPerf') === '1'
    || params.get('startupPerf') === '1'
    || sessionStorage.getItem(STORAGE_KEY) === '1'
    || localStorage.getItem(STORAGE_KEY) === '1'
    || isLocal;
}

export function markStartupPerf(name: string, data?: Record<string, any>) {
  const time = performance.now();
  const entry: StartupPerfEntry = {
    name,
    time,
    delta: time - START_TIME,
    data,
  };

  entries.push(entry);
  // eslint-disable-next-line no-underscore-dangle
  window.__SSG_STARTUP_PERF__ = entries;

  if (STARTUP_PHASE_MARKS.includes(name)
    && STARTUP_PHASE_MARKS.every((required) => entries.some((item) => item.name === required))) {
    reportStartupPhases();
  }

  if (!isStartupPerfEnabled()) return;

  // eslint-disable-next-line no-console
  console.log(`[SSG_PERF] ${name} +${entry.delta.toFixed(1)}ms`, data || '');

  logSegmentsFor(name);
}

export function printStartupPerfSummary() {
  if (!isStartupPerfEnabled()) return;

  if (!entries.length) return;

  // eslint-disable-next-line no-console
  console.table(entries.map((entry) => ({
    name: entry.name,
    deltaMs: Number(entry.delta.toFixed(1)),
    timeMs: Number(entry.time.toFixed(1)),
    data: entry.data ? JSON.stringify(entry.data) : '',
  })));
}

function markAt(name: string) {
  const entry = entries.find((item) => item.name === name);
  return entry ? entry.time : undefined;
}

function at(name: string) {
  const value = markAt(name);
  return value === undefined ? -1 : Math.round(value);
}

function getScriptTransfer() {
  try {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const scripts = resources.filter((item) => item.name.includes('.js'));
    const bytes = scripts.reduce((acc, item) => acc + (item.transferSize || 0), 0);
    return { count: scripts.length, transferKb: Math.round(bytes / 1024) };
  } catch {
    return { count: 0, transferKb: 0 };
  }
}

export function reportStartupPhases() {
  if (isStartupPhaseReported) return;
  isStartupPhaseReported = true;

  const renderDone = markAt('react_render_done');
  if (renderDone === undefined) return;

  const { count, transferKb } = getScriptTransfer();
  const milestones = [
    `boot:${at('init_start')}`,
    `glb:${at('init_global_done')}`,
    `rd:${Math.round(renderDone)}`,
    `wkb:${at('worker_boot_start')}`,
    `wk:${at('worker_first_message')}`,
    `api:${at('api_first_response')}`,
    `ui:${at('ui_loader_ready')}`,
    `sp:${at('splash_removed')}`,
    `hidden:${Math.round(getHiddenMsSinceLoad())}`,
    `js:${transferKb} jsn:${count}`,
  ];
  const readyAt = Math.max(at('api_first_response'), at('ui_loader_ready'), at('splash_removed'));

  recordClientDiagnosticEvent({
    type: 'startup_phase',
    reason: milestones.join(' '),
    success: true,
    duration: readyAt > 0 ? readyAt : Math.round(renderDone),
  });
}

export function enableStartupPerfForCurrentSession() {
  sessionStorage.setItem(STORAGE_KEY, '1');
}

export function disableStartupPerfForCurrentSession() {
  sessionStorage.removeItem(STORAGE_KEY);
}
